// TODO: исправить ошибки данного правила
/* eslint-disable react-hooks/rules-of-hooks */
import cn from "classnames";
import React, {
  useMemo,
  useState,
  useEffect,
  forwardRef,
  useCallback,
  FocusEvent,
} from "react";
import { mergeRefs } from "../common/utils/merge-refs";
import { getCurrencySymbol } from "../common/currency/utils";
import { Input } from "../input";
import inputStyles from "../input/styles.module.scss";
import { Portal } from "../common/components/portal";
import { IconPressEvent } from "../input/types";
import { Text } from "../text";
import { LimitsHint } from "./limits-hint";
import {
  clearValue,
  splitAmountValue,
  formatAmountValue,
  isValidNumericValue,
  handleInputValueChange,
  validateLimits,
} from "./utils";
import { useInputState } from "./hooks";
import { AmountInputProps } from "./types";
import { DECIMAL_SEPARATOR } from "./constants";
import styles from "./styles.module.scss";

export const AmountInput = forwardRef<HTMLInputElement, AmountInputProps>(
  (
    {
      hint,
      value,
      label,
      error,
      className,
      minValue = null,
      maxValue = null,
      currency = "RUB",
      dataTestId = "amount-input",
      disabled,
      onCopy,
      onPaste,
      onChange,
      onKeyDown,
      onClear,
      onBlur,
      ...restProps
    },
    forwardedRef
  ) => {
    const isCorrectTypeOfAmountValue = isValidNumericValue(value);

    if (!isCorrectTypeOfAmountValue) {
      return null;
    }

    const [lastKeyStroke, setLastKeyStroke] = useState<string | null>(null);

    const formattedValue = formatAmountValue(value);
    const { inputRef, setInputState, getLastInputState, getInputState } =
      useInputState(formattedValue);

    const handleChangeInputState = useCallback(
      (value: string, caretPosition: number) => {
        setInputState({
          value,
          caretPosition,
        });
      },
      [setInputState]
    );

    useEffect(() => {
      const correctValue = validateLimits({
        value: formattedValue,
        maxValue,
        minValue,
      });

      if (correctValue !== getInputState().value) {
        handleChangeInputState(correctValue, getInputState().caretPosition);
      }
    }, [formattedValue, handleChangeInputState, maxValue, minValue]);

    const currencySymbol = getCurrencySymbol(currency);

    const currencyIcon = useMemo(() => {
      if (currencySymbol === null) {
        return;
      }

      const icon = (
        <Text
          size="medium"
          className={cn(styles["currency-icon"], {
            [styles.disabled]: disabled,
          })}
          dataTestId={`${dataTestId}-currency-icon`}
        >
          {currencySymbol}
        </Text>
      );

      return icon;
    }, [currencySymbol, disabled, dataTestId]);

    const limitsHint = useMemo(
      () => (
        <LimitsHint
          minValue={minValue}
          maxValue={maxValue}
          dataTestId={dataTestId}
          currencySymbol={currencySymbol}
          className={styles["limits-hint"]}
        />
      ),
      [minValue, maxValue, currencySymbol]
    );

    const handleChange = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value, selectionStart } = event.target;

        const { cursorPosition, value: formattedValue } =
          handleInputValueChange({
            lastInputState: getLastInputState(),
            lastKeyStroke,
            selectionStart: selectionStart ?? value.length,
            value,
          });

        handleChangeInputState(formattedValue, cursorPosition);
        setLastKeyStroke(null);

        onChange?.(event, {
          value: clearValue(formattedValue),
          unmaskedValue: formattedValue,
        });
      },
      [
        onChange,
        setInputState,
        getLastInputState,
        lastKeyStroke,
        handleChangeInputState,
      ]
    );

    const handleKeyDown = useCallback(
      (event: React.KeyboardEvent<HTMLInputElement>) => {
        setLastKeyStroke(event.key);
        onKeyDown?.(event);
      },
      [onKeyDown]
    );

    const handleCopy = useCallback(
      (event: React.ClipboardEvent<HTMLInputElement>) => {
        if (value != null) {
          navigator.clipboard.writeText(value.toString());
        }

        onCopy?.(event);
      },
      [onCopy, value]
    );

    const handlePaste = useCallback(
      (event: React.ClipboardEvent<HTMLInputElement>) => {
        setLastKeyStroke(null);
        onPaste?.(event);
      },
      [onPaste]
    );

    const handleClear = (event: IconPressEvent) => {
      setInputState({
        value: "",
        caretPosition: 0,
      });

      onClear?.(event);
    };

    const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
      const currentValue = getInputState().value;
      const correctValue = validateLimits({
        value: currentValue,
        maxValue,
        minValue,
      });

      if (currentValue !== correctValue) {
        handleChangeInputState(correctValue, getInputState().caretPosition);
      }

      onBlur?.(event);
    };

    const getPortalContainer = useCallback(
      () => inputRef.current?.parentElement,
      []
    );

    const displayValue = getInputState().value;
    const [integerPart, decimalPart] = splitAmountValue(displayValue);

    const isDecimalPartVisible = decimalPart !== undefined;

    const portalContent = (
      <div
        className={cn(inputStyles.input, styles["input-value-portal"], {
          [inputStyles.labeled]: label,
        })}
        data-test-id={`${dataTestId}-portal`}
      >
        <Text className={styles["input-content-wrapper"]}>
          <Text
            size="medium"
            className={cn(styles["integer-part"], {
              [styles.disabled]: disabled,
            })}
          >
            {integerPart}
          </Text>
          {isDecimalPartVisible && (
            <Text
              size="medium"
              className={cn(styles["decimal-part"], {
                [styles.disabled]: disabled,
              })}
              dataTestId={`${dataTestId}-decimal-part`}
            >
              {`${DECIMAL_SEPARATOR}${decimalPart}`}
            </Text>
          )}
          {currencyIcon}
        </Text>
      </div>
    );

    return (
      <>
        <Input
          type="text"
          hint={hint || limitsHint}
          error={error}
          label={label}
          disabled={disabled}
          className={cn(className, {
            [styles["hidden-input-value"]]: displayValue,
          })}
          dataTestId={dataTestId}
          value={displayValue}
          onCopy={handleCopy}
          onPaste={handlePaste}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onClear={handleClear}
          ref={mergeRefs(forwardedRef, inputRef)}
          onBlur={handleBlur}
          {...restProps}
        />
        {displayValue && (
          <Portal getContainer={getPortalContainer}>{portalContent}</Portal>
        )}
      </>
    );
  }
);
